import { BRAND_COPY } from '@fyf/shared';

import { ScreenLayout } from '../components/ScreenLayout.js';
import { useCaptureDispatch, useCaptureState } from '../context/CaptureFlowContext.js';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard.js';

const { share } = BRAND_COPY.screens;

function buildShareMessage(
  surfaceName: string | null,
  superpower: string | null,
  feeling: string | null,
  eventHashtag: string | null
): string {
  return share.messageTemplate
    .replace('{surfaceName}', surfaceName ?? 'the event')
    .replace('{superpower}', superpower ?? 'my superpower')
    .replace('{feeling}', feeling ?? 'inspired')
    .replace('{eventHashtag}', eventHashtag ?? '')
    .replace(/  +/g, ' ')
    .trim();
}

export function ShareScreen() {
  const { flameImageUrl, superpower, feeling, surfaceName, eventHashtag } = useCaptureState();
  const dispatch = useCaptureDispatch();
  const { copied, copy } = useCopyToClipboard();

  const message = buildShareMessage(surfaceName, superpower, feeling, eventHashtag);
  const copyText = flameImageUrl ? `${message}\n\n${flameImageUrl}` : message;

  return (
    <ScreenLayout glowIntensity="high">
      <div className="flex flex-col gap-4 items-center w-full pb-4">
        {/* Flame image with back button overlay */}
        <div className="relative w-full flex flex-col items-center">
          <button
            onClick={() => dispatch({ type: 'SET_SCREEN', payload: 'reveal' })}
            className="absolute top-0 left-0 z-10 flex items-center justify-center w-[32px] h-[32px] rounded-full border border-[#757575] hover:border-[#F9F7F4] transition-colors"
            aria-label="Back to your flame"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-[#F9F7F4]"
            >
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          {flameImageUrl && (
            <img
              src={flameImageUrl}
              alt="Your flame"
              className="w-full max-w-[250px] max-h-[250px] rounded-full object-cover"
              style={{ boxShadow: '0 0 26.4px 0.66px #ff5e00' }}
            />
          )}
        </div>

        {/* Superpower + Feeling (inline) */}
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-end justify-center gap-4">
            <p className="font-['DM_Sans',sans-serif] text-xs uppercase text-[#B8B8B8] tracking-wider whitespace-nowrap">
              {share.superpowerLabel}
            </p>
            <p className="font-['DM_Sans',sans-serif] text-lg text-[#F97216] leading-none">
              {superpower}
            </p>
          </div>
          <div className="flex items-end justify-center gap-4">
            <p className="font-['DM_Sans',sans-serif] text-xs uppercase text-[#B8B8B8] tracking-wider whitespace-nowrap">
              {share.feelingLabel}
            </p>
            <p className="font-['DM_Sans',sans-serif] text-lg text-[#F97216] leading-none">
              {feeling}
            </p>
          </div>
        </div>

        {/* Share message */}
        <div className="w-full">
          <p className="font-['DM_Sans',sans-serif] text-sm text-[#F9F7F4] mb-2">
            {share.shareMessageLabel}
          </p>
          <div className="border border-[#757575] rounded-[10px] p-4">
            <p className="font-['DM_Sans',sans-serif] text-sm text-[#F9F7F4] leading-5 whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>

        {/* LinkedIn + Copy */}
        <div className="flex items-center justify-between w-full">
          <button
            onClick={() => window.open('https://www.linkedin.com', '_blank')}
            className="flex items-center gap-2 rounded-full border border-[#757575] py-2 px-3 font-['DM_Sans',sans-serif] text-xs font-medium text-[#F9F7F4] hover:border-[#f97216] transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            Share on LinkedIn
          </button>

          <button
            onClick={() => copy(copyText)}
            className="flex items-center gap-2 rounded-full border border-[#757575] py-2 px-3 font-['DM_Sans',sans-serif] text-xs font-medium text-[#F9F7F4] hover:border-[#f97216] transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
            </svg>
            {copied ? share.copiedButton : share.copyButton}
          </button>
        </div>
      </div>
    </ScreenLayout>
  );
}
